import { Check, Copy, Crown, ExternalLink, LoaderCircle, LogOut, Play, Wifi, WifiOff } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useState } from 'react';
import { TokenSprite } from './TokenSprite.jsx';

export function WaitingRoom({ room, action, leaveLocalSession, connected, onReturn }) {
  const { game, code, hostId, viewerId } = room;
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const isHost = viewerId === hostId;
  const botCount = game.players.filter((player) => player.isBot).length;
  const humanCount = game.players.length - botCount;
  const joinUrl = `${(room.networkUrl || window.location.origin).replace(/\/$/, '')}/join/${code}`;

  const copyCode = async () => {
    await navigator.clipboard?.writeText(code);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1_500);
  };

  const start = async () => {
    setBusy(true);
    try {
      await action('room:start');
    } catch {
      // Общий toast уже содержит сообщение сервера.
    } finally {
      setBusy(false);
    }
  };

  const configureBots = async (count) => {
    setBusy(true);
    try { await action('room:bots', { count }); } catch { /* Общий toast. */ }
    finally { setBusy(false); }
  };

  const leave = async () => {
    if (!window.confirm('Покинуть комнату и удалить сохранённый вход на этом устройстве?')) return;
    setBusy(true);
    try { await leaveLocalSession(); } catch { /* Credentials remain available; the global toast explains the failure. */ }
    finally { setBusy(false); }
  };

  return (
    <main className="waiting-shell">
      <header className="waiting-header">
        <div className="brand-lockup"><span className="brand-mark" aria-hidden="true"><i /><i /><i /></span><span>Капиталия</span></div>
        <button className="button button--ghost" type="button" onClick={onReturn} disabled={busy}>Меню</button>
        <button className="button button--ghost" type="button" onClick={leave} disabled={busy || !connected}><LogOut size={17} /> Покинуть комнату</button>
      </header>
      <section className="waiting-card">
        <div className="waiting-copy">
          <span className={connected ? 'connection-badge' : 'connection-badge connection-badge--offline'}>
            {connected ? <Wifi size={15} /> : <WifiOff size={15} />}{connected ? 'Комната доступна' : 'Переподключение'}
          </span>
          <h1>Соберите игроков</h1>
          <p>Откройте адрес сервера на другом устройстве и введите этот код.</p>
          <button className="room-code" type="button" onClick={copyCode} aria-label="Скопировать код комнаты">
            {code.split('').map((letter, index) => <span key={`${letter}-${index}`}>{letter}</span>)}
            <i>{copied ? <Check size={18} /> : <Copy size={18} />}</i>
          </button>
          <div className="room-invite">
            <div className="room-invite__qr" aria-label={`QR-код для входа в комнату ${code}`}>
              <QRCodeSVG value={joinUrl} size={154} level="M" bgColor="#fffdf7" fgColor="#0b2942" marginSize={1} />
            </div>
            <div>
              <span>Сканируйте камерой телефона</span>
              <strong>{joinUrl}</strong>
              <button type="button" onClick={() => navigator.clipboard?.writeText(joinUrl)}><Copy size={15} /> Скопировать ссылку</button>
              <a href={joinUrl} target="_blank" rel="noreferrer"><ExternalLink size={15} /> Открыть</a>
            </div>
          </div>
        </div>

        <div className="waiting-players">
          <h2>Игроки <span>{game.players.length}/{game.settings.maxPlayers}</span></h2>
          <div className="waiting-player-list">
            {game.players.map((player) => (
              <article className="waiting-player" key={player.id}>
                <TokenSprite token={player.token} size="lg" />
                <div><strong>{player.name}</strong><span>{player.isBot ? 'Бот · управляется сервером' : player.id === hostId ? 'Хозяин комнаты' : 'Готов к игре'}</span></div>
                {player.id === hostId && <Crown size={19} aria-label="Хозяин" />}
                <span className={`status-dot ${player.connectionStatus === 'online' ? 'status-dot--online' : player.connectionStatus === 'reconnecting' ? 'status-dot--reconnecting' : ''}`} title={player.connectionStatus === 'reconnecting' ? 'Переподключение' : player.connected ? 'Онлайн' : 'Не в сети'} />
              </article>
            ))}
            {Array.from({ length: Math.max(0, 2 - game.players.length) }, (_, index) => (
              <article className="waiting-player waiting-player--empty" key={index}>
                <LoaderCircle className="spin-slow" size={27} />
                <span>Ждём ещё игрока…</span>
              </article>
            ))}
          </div>
          {isHost && <div className="bot-setup">
            <label htmlFor="bot-count">Количество ботов</label>
            <select id="bot-count" value={botCount} disabled={busy} onChange={(event) => configureBots(Number(event.target.value))}>
              {Array.from({ length: game.settings.maxPlayers - humanCount + 1 }, (_, count) => <option key={count} value={count}>{count}</option>)}
            </select>
            <small>Люди: {humanCount} · Боты: {botCount} · Всего: {game.players.length}/4{room.isSpectator ? ' · Вы наблюдатель' : ''}</small>
          </div>}
          {isHost ? (
            <button className="button button--primary button--wide" type="button" onClick={start} disabled={game.players.length < 2 || game.players.some((player) => !player.connected) || busy}>
              <Play size={19} fill="currentColor" /> {busy ? 'Запускаем…' : 'Начать игру'}
            </button>
          ) : (
            <p className="host-wait"><LoaderCircle className="spin-slow" size={18} /> Хозяин начнёт матч, когда все соберутся.</p>
          )}
        </div>
      </section>
    </main>
  );
}
